import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import ErrorDisplay from '../components/ErrorDisplay';
import LoadingOverlay from '../components/LoadingOverlay';

const LOGIN_LOCKOUT_STORAGE_KEY = 'loginLockoutResetAt';

const getRemainingSeconds = (resetAt) => {
  const resetAtMs = Date.parse(resetAt);
  if (!Number.isFinite(resetAtMs)) return 0;
  return Math.max(0, Math.ceil((resetAtMs - Date.now()) / 1000));
};

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const loadStoredLockout = () => {
  if (typeof window === 'undefined') return { resetAt: null, seconds: 0 };

  const resetAt = window.localStorage.getItem(LOGIN_LOCKOUT_STORAGE_KEY);
  const seconds = getRemainingSeconds(resetAt);
  if (seconds === 0) {
    window.localStorage.removeItem(LOGIN_LOCKOUT_STORAGE_KEY);
    return { resetAt: null, seconds: 0 };
  }

  return { resetAt, seconds };
};

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [loginLockout, setLoginLockout] = useState(loadStoredLockout);
  const { login } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors
  } = useForm();

  useEffect(() => {
    if (!loginLockout.resetAt) return undefined;

    const updateCountdown = () => {
      const seconds = getRemainingSeconds(loginLockout.resetAt);
      if (seconds === 0) {
        window.localStorage.removeItem(LOGIN_LOCKOUT_STORAGE_KEY);
        setLoginLockout({ resetAt: null, seconds: 0 });
        clearErrors('root');
        return;
      }

      setLoginLockout((current) => ({ ...current, seconds }));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [clearErrors, loginLockout.resetAt]);

  const onSubmit = async (data, event) => {
    // AGGRESSIVE: Prevent any form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('🚀 LOGIN: Form submission started');
    setIsLoading(true);
    setApiError(null);
    
    // Add a small delay to ensure UI updates
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      console.log('🚀 LOGIN: Attempting login with:', { email: data.email });
      const result = await login(data.email, data.password);
      
      if (!result.success) {
        console.error('❌ LOGIN: Login failed:', result);
        
        if (result.status === 429) {
          const fallbackResetAt = new Date(
            Date.now() + Math.max(1, Number(result.retryAfter) || 900) * 1000
          ).toISOString();
          const resetAt = getRemainingSeconds(result.resetAt) > 0
            ? result.resetAt
            : fallbackResetAt;

          window.localStorage.setItem(LOGIN_LOCKOUT_STORAGE_KEY, resetAt);
          setLoginLockout({
            resetAt,
            seconds: getRemainingSeconds(resetAt)
          });
        }

        // Set the full error for detailed display
        if (result.fullError) {
          setApiError(result.fullError);
        }
        
        if (result.details && Array.isArray(result.details)) {
          // Handle validation errors
          result.details.forEach(error => {
            setError(error.field, {
              type: 'server',
              message: error.message
            });
          });
        } else {
          setError('root', {
            type: 'server',
            message: result.error
          });
        }
      } else {
        console.log('✅ LOGIN: Login successful');
      }
    } catch (error) {
      console.error('❌ LOGIN: Unexpected error:', error);
      setApiError(error);
      setError('root', {
        type: 'server',
        message: 'An unexpected error occurred'
      });
    } finally {
      console.log('🏁 LOGIN: Form submission completed');
      setIsLoading(false);
    }
  };

  // Handle form submission with additional prevention
  const handleFormSubmit = (event) => {
    console.log('🛑 FORM SUBMIT HANDLER CALLED');
    event.preventDefault();
    event.stopPropagation();
    
    // Use handleSubmit from react-hook-form
    handleSubmit(onSubmit)(event);
    
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="hidden sm:block text-sm text-gray-600">Cloud-Based FBR Digital Invoicing</p>
              </div>
            </Link>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link
                to="/"
                className="px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
              >
                ← Back to Home
              </Link>
              <Link
                to="/register"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                Welcome Back
              </h2>
              <p className="mt-2 text-gray-600">
                Sign in to access your FBR invoicing dashboard
              </p>
            </div>
        
            <form className="mt-8 space-y-6" onSubmit={handleFormSubmit}>
              {/* Enhanced Error Display */}
              {apiError && (
                <ErrorDisplay 
                  error={apiError} 
                  onClose={() => setApiError(null)}
                  className="mb-4"
                />
              )}
              
              {errors.root && !apiError && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {errors.root.message}
                  </div>
                </div>
              )}

              {loginLockout.seconds > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4" role="status" aria-live="polite">
                  <p className="text-sm font-medium text-amber-800">
                    Login temporarily locked. Try again in {formatCountdown(loginLockout.seconds)}.
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    Attempts reset at {new Date(loginLockout.resetAt).toLocaleTimeString()}.
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="label">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`input-field ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter your email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                  {errors.email && (
                    <p className="error-text">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`input-field pr-10 ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter your password"
                      {...register('password', {
                        required: 'Password is required'
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="error-text">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading || loginLockout.seconds > 0}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : loginLockout.seconds > 0 ? (
                    `Try again in ${formatCountdown(loginLockout.seconds)}`
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>

              {/* Development Error Testing */}
              {process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_DEV_TOOLS === 'true' && (
                <div className="text-center space-y-2">
                  <div className="text-xs text-gray-500 mb-2">Development Testing:</div>
                  <div className="flex space-x-2 justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🧪 Testing API Error...');
                        setApiError({
                          response: {
                            status: 401,
                            data: {
                              error: 'FBR Authentication Failed',
                              message: 'Invalid or expired FBR API token',
                              details: {
                                environment: 'sandbox',
                                tokenStatus: 'INVALID',
                                solution: 'Please update your sandbox FBR API token in Profile > FBR API Configuration',
                                configurationRequired: true
                              }
                            }
                          }
                        });
                      }}
                      className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    >
                      Test FBR Error
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        console.log('🧪 Testing Network Error...');
                        setApiError({
                          message: 'Network Error',
                          code: 'ECONNREFUSED'
                        });
                      }}
                      className="text-xs px-2 py-1 bg-yellow-100 text-yellow-600 rounded hover:bg-yellow-200"
                    >
                      Test Network Error
                    </button>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link
                    to="/register"
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    Register here
                  </Link>
                </p>
              </div>
            </form>

            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  FBRSTAPP Cloud Platform
                </h3>
                <p className="text-sm text-gray-600">
                  Submit invoices directly to Pakistan's Federal Board of Revenue (FBR) 
                  through our secure cloud platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Loading Overlay */}
      <LoadingOverlay 
        isVisible={isLoading}
        message="Signing in..."
        details="Please wait while we authenticate your credentials"
      />
    </div>
  );
};

export default Login;
